import React from 'react'
import {Button, Flex, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedMessage, defineMessage} from 'react-intl'
import ProductRegistrationCard from './partials/product-registration-card'
import {GuaranteedIcon, SupportIcon, DiscountIcon, ManualsIcon} from '../../components/icons'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import ProductRegistrationHeader from './partials/product-registration-header'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import ProductRegistrationImageContainer from './partials/product-registration-image-container'
import {useCustomPreferenceWithLocale} from '../../hooks/use-custom-preference'
import routingObject from '../../utils/static-routes'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useLocation} from 'react-router'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Helmet} from 'react-helmet'
import {useProductRegistrationPageMeta} from '../../hooks/use-category-meta'

const ProductRegistration = () => {
    const {site} = useMultiSite()
    const brandName = site.id.split('-')[0]

    const styles = useMultiStyleConfig('ProductRegistration')
    const isWarrantyBox = useCustomPreferenceWithLocale('showWarrantyBox')
    const isWarrantyBoxEnabled = isWarrantyBox?.box === 'true'

    const location = useLocation()
    const {customParams} = getConfig()
    const currentPath = 'https://' + customParams.currentHost + location.pathname
    const {metaTitle, metaDescription} = useProductRegistrationPageMeta()

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: metaTitle,
        description: metaDescription,
        url: currentPath,
    }

    const productRegistrationTitleMessage = defineMessage({
        id: 'product_registration.title',
        defaultMessage: 'Register your product',
    })

    const navigate = useNavigation()
    const handleButtonClick = () => {
        navigate(`${routingObject.PRODUCT_REGISTRATION_PATH}/search`)
    }

    let productRegistrationImage
    switch (brandName) {
        case 'kenwood':
            productRegistrationImage = getAssetUrl(
                `static/img/product-registration/productRegistrationImage-kenwood.png`,
            )
            break
        default: // delonghi image is fallback
            productRegistrationImage = getAssetUrl(
                `static/img/product-registration/productRegistrationImage-delonghi.png`,
            )
    }

    const registerTitle = (
        <FormattedMessage
            id="product_registration.infoTitle"
            defaultMessage="Register a coffee machine"
        />
    )
    const registerDescription = (
        <FormattedMessage
            id="product_registration.infoDescription"
            defaultMessage="If you are registering a coffee machine, you can start the product registration by <b>scanning the Product Registration QR code code on your product box</b>, for an hassle-free experience (we will prefill all product data for you)."
            values={{
                b: (chunks) => <b>{chunks}</b>,
            }}
        />
    )

    const cardsData = [
        isWarrantyBoxEnabled && {
            icon: GuaranteedIcon,
            description: (
                <FormattedMessage
                    id="product_registration.guaranteedIcon_description"
                    defaultMessage="Extended warranty on selected items. Check <u>eligible products</u>"
                    values={{
                        u: (chunks) => (
                            <u>
                                <a href={isWarrantyBox?.hreflink}>{chunks}</a>
                            </u>
                        ),
                    }}
                />
            ),
            testId: 'warranty-card',
        },
        {
            icon: ManualsIcon,
            description: (
                <FormattedMessage
                    id="product_registration.icon360_description"
                    defaultMessage="Easy access to your product information and helpful tips"
                />
            ),
            testId: 'manuals-card',
        },
        {
            icon: DiscountIcon,
            description: (
                <FormattedMessage
                    id="product_registration.discountIcon_description"
                    defaultMessage="Exclusive offers with newsletter sign up"
                />
            ),
            testId: 'discount-card',
        },
        {
            icon: SupportIcon,
            description: (
                <FormattedMessage
                    id="product_registration.supportIcon_description"
                    defaultMessage="Direct access to our dedicated product service and support teams"
                />
            ),
            testId: 'support-card',
        },
    ].filter(Boolean)

    return (
        <Flex {...styles.container} layerStyle={'page'}>
            <Helmet>
                {metaTitle && <title>{metaTitle}</title>}

                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            <ProductRegistrationHeader />
            <Flex {...styles.cardsContainerDesktop}>
                {cardsData.map((card, index) => (
                    <ProductRegistrationCard
                        key={index}
                        icon={card.icon}
                        description={card.description}
                        datatestid={card.testId}
                    />
                ))}
            </Flex>
            <Flex {...styles.labelAndButtonContainer}>
                <Text {...styles.buttonLabel} data-testid="button-label">
                    <FormattedMessage
                        id="product_registration.button_label"
                        defaultMessage="Before starting your product registration, please <b>make sure you have your product at hand</b>, as you will need to input some product information (model number, serial number)."
                        values={{
                            b: (chunks) => <b>{chunks}</b>,
                        }}
                    />
                </Text>
                <Button
                    onClick={handleButtonClick}
                    variant="primary"
                    {...styles.button}
                    data-testid="register-button"
                >
                    <Text {...styles.buttonText}>
                        <FormattedMessage {...productRegistrationTitleMessage} />
                    </Text>
                </Button>
            </Flex>
            <Flex {...styles.cardsContainerMobile}>
                {cardsData.map((card, index) => (
                    <ProductRegistrationCard
                        key={index}
                        icon={card.icon}
                        description={card.description}
                        data-testid={card.testId}
                    />
                ))}
            </Flex>
            <ProductRegistrationImageContainer
                id="product-registration-index-image"
                image={productRegistrationImage}
                title={registerTitle}
                description={registerDescription}
            />
        </Flex>
    )
}

export default ProductRegistration
